import _ from 'underscore';
import React, { useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Text, Stack } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { MultiLanguageSelector } from '@bingads-webui-campaign-react/multi-language-selector';
import { SelectedItem } from '@bingads-webui-campaign-react/pill-item-components';
import { globalizationModelSetup } from '@bingads-webui-campaign/ads-globalization';
import { campaignLanguageStrings } from './constants';
import { getIntentLanguageStyles } from '../styles/get-intent-language-style';

export function LanguageTarget(props) {
  const {
    selectedLanguages,
    setSelectedLanguages,
    permissions,
    locale,
    showLabel,
  } = props;

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getIntentLanguageStyles);
  const { IsDomesticSimplifiedChineseEnabled } = permissions;
  const { LANGUAGES: supportedLanguages } = globalizationModelSetup({ permissions, culture: locale });
  // TO DO Add back when ads globalization is GA-ed
  const languages = useMemo(() => {
    let languageList = _.chain(AdGroup.properties.Language.enum)
      .filter(language => _.contains(supportedLanguages, language))
      .sortBy()
      .value();

    if (!IsDomesticSimplifiedChineseEnabled) {
      languageList = ['All', ...languageList]; // make 'All' the first language
    }

    return languageList.map(language => ({
      key: language,
      text: i18n.getString(campaignLanguageStrings[language]),
    }));
  }, [i18n, supportedLanguages, IsDomesticSimplifiedChineseEnabled]);

  const onDeleteLanguage = useCallback((index) => {
    const newSelectedLanguages = _(selectedLanguages).clone();
    newSelectedLanguages.splice(index, 1);
    setSelectedLanguages(newSelectedLanguages);
  }, [selectedLanguages, setSelectedLanguages]);

  const languageAll = 'All';
  const onSelectedLanguagesChanged = useCallback((event, item) => {
    if (item) {
      if (item.selected && item.key === languageAll) {
        setSelectedLanguages([languageAll]);
      } else {
        setSelectedLanguages(item.selected ? [...selectedLanguages, item.key] :
          selectedLanguages.filter(key => key !== item.key));
      }
    }
  }, [selectedLanguages, setSelectedLanguages]);

  const label = i18n.getString(_TL_('Select the language of the websites where you want your ads to appear.'));
  const stackContainerToken = {
    childrenGap: 8,
  };
  return (
    <div className={classNames('language-target', classes.intentTarget)}>
      <div className="select-language-label-container">
        {showLabel && <Text>{label}</Text>}
      </div>
      <div className={classNames('languages-selected-items', classes.preSelectedItemsStyle)}>
        <Stack
          horizontal
          wrap
          disableShrink
          tokens={stackContainerToken}
        >
          {selectedLanguages.map((item, index) => (
            <SelectedItem
              key={i18n.getString(campaignLanguageStrings[item])}
              itemText={i18n.getString(campaignLanguageStrings[item])}
              itemIndex={index}
              onDelete={onDeleteLanguage}
            />
          ))}
        </Stack>
      </div>
      <MultiLanguageSelector
        languages={languages}
        selectedLanguages={selectedLanguages}
        onSelectedLanguagesChanged={onSelectedLanguagesChanged}
        ariaLabel={label}
      />
    </div>
  );
}

LanguageTarget.propTypes = {
  selectedLanguages: PropTypes.arrayOf(PropTypes.string),
  setSelectedLanguages: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  permissions: PropTypes.shape({
    IsDomesticSimplifiedChineseEnabled: PropTypes.bool,
  }).isRequired,
  showLabel: PropTypes.bool,
};

LanguageTarget.defaultProps = {
  selectedLanguages: [],
  showLabel: true,
};
